// src/components/policies/TermlyEmbed.js
import React, { useEffect, useRef, useState } from 'react';

const TERM_SCRIPT_ID  = 'termly-jssdk';
const TERM_SCRIPT_SRC = 'https://app.termly.io/embed-policy.min.js';

const reloadTermlyScript = () =>
  new Promise((resolve, reject) => {
    const stale = document.getElementById(TERM_SCRIPT_ID);
    if (stale) stale.remove();

    // Clear any cached Termly global so the re-added script fully reinitialises
    try { delete window.Termly; } catch (_) { window.Termly = undefined; }

    const script    = document.createElement('script');
    script.id       = TERM_SCRIPT_ID;
    script.type     = 'text/javascript';
    script.src      = `${TERM_SCRIPT_SRC}?v=${Date.now()}`;
    script.async    = true;
    script.onload   = () => resolve();
    script.onerror  = () => reject(new Error('Termly script failed to load.'));
    document.body.appendChild(script);
  });

/* ─── component ─────────────────────────────────────────────────────────────── */
const TermlyEmbed = ({ dataId }) => {
  const [loading,  setLoading]  = useState(true);
  const [hasError, setHasError] = useState(false);
  const embedRef               = useRef(null);
  const mountedRef             = useRef(false);

  const run = () => {
    if (!mountedRef.current) return;
    setLoading(true);
    setHasError(false);

    // MutationObserver — fires the moment Termly injects the iframe / content
    let observer = null;
    if (embedRef.current) {
      observer = new MutationObserver(() => {
        if (embedRef.current && embedRef.current.childElementCount > 0) {
          observer.disconnect();
          if (mountedRef.current) setLoading(false);
        }
      });
      observer.observe(embedRef.current, { childList: true, subtree: true });
    }

    // Hard fallback — stop waiting after 12 s
    const timeout = setTimeout(() => {
      if (!mountedRef.current) return;
      observer?.disconnect();
      if (embedRef.current && embedRef.current.childElementCount > 0) {
        setLoading(false);
      } else {
        setLoading(false);
        setHasError(true);
      }
    }, 12000);

    reloadTermlyScript()
      .then(() => {
        if (!mountedRef.current) return;
        // Give Termly a tick to scan the DOM after its script executes
        setTimeout(() => {
          if (typeof window.Termly?.initialize === 'function') {
            window.Termly.initialize();
          }
        }, 100);
      })
      .catch(() => {
        if (!mountedRef.current) return;
        clearTimeout(timeout);
        observer?.disconnect();
        setLoading(false);
        setHasError(true);
      });

    return () => {
      clearTimeout(timeout);
      observer?.disconnect();
    };
  };

  useEffect(() => {
    mountedRef.current = true;
    const cleanup = run();
    return () => {
      mountedRef.current = false;
      cleanup?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataId]);

  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-12">
      {/* Wrapper: relative so the spinner can float above the embed */}
      <div className="relative min-h-[320px]">

        {/* Loading overlay — sits on top of the Termly div so the div stays
            at its natural size and the iframe can render correctly */}
        {loading && !hasError && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 bg-white">
            <svg
              className="animate-spin w-8 h-8 text-brand-red"
              xmlns="http://www.w3.org/2000/svg"
              fill="none" viewBox="0 0 24 24"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10"
                stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
            </svg>
            <p className="text-sm text-gray-400">Loading policy…</p>
          </div>
        )}

        {/* Termly target — always in the DOM at full size so the injected
            iframe has room to render. Never hidden or collapsed. */}
        <div
          ref={embedRef}
          name="termly-embed"
          data-id={dataId}
        />

        {/* Error state */}
        {hasError && (
          <div className="rounded-xl border border-gray-200 bg-gray-50 px-6 py-10 text-center">
            <p className="text-sm font-semibold text-gray-800 mb-2">
              This policy couldn&rsquo;t be loaded right now.
            </p>
            <p className="text-xs text-gray-500 leading-relaxed max-w-sm mx-auto">
              This is usually caused by a browser content-blocker (uBlock Origin,
              Privacy Badger, etc.). Try disabling extensions for this page and
              refreshing.
            </p>
            <button
              onClick={() => run()}
              className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-red text-white text-xs font-semibold hover:bg-red-700 transition-colors"
            >
              Try again
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TermlyEmbed;
